import { useState, useMemo, useCallback, useEffect } from 'react';
import { usePersistedFilterState } from '@/hooks/usePersistedFilterState';
import { FilterRule, FilterField, FilterGroup, FilterEntity, FilterAttribute } from '@/types/filters';
import { ALL_CREDIT_REASON_TYPES } from '@/components/customers/credits/CreateManualCreditModal';
import { supabase } from '@/integrations/supabase/client';

type Option = { value: string; label: string };

interface CreditWithCustomer {
  id: string;
  credit_amount: number;
  remaining_amount: number;
  source_type: string;
  source_invoice_id: string | null;
  description: string | null;
  status: string;
  credit_note_number: string | null;
  credit_note_issued_at: string | null;
  reason_type: string | null;
  created_at: string;
  customer: {
    id: string;
    name: string;
    last_name: string;
    city: string | null;
    province_state: string | null;
    suburb: string | null;
    tags: string[] | null;
    postal_code: string | null;
    customer_type: string;
    lead_source: string | null;
    emails: string[];
    cell_numbers: string[];
  } | null;
  source_invoice: {
    invoice_number: string | null;
  } | null;
}

export const useCreditNoteFilterBuilder = (credits: CreditWithCustomer[]) => {
  const [draftGroups, setDraftGroups] = usePersistedFilterState<FilterGroup[]>('filters-credit-notes-draft', []);
  const [appliedGroups, setAppliedGroups] = usePersistedFilterState<FilterGroup[]>('filters-credit-notes-applied', []);

  const [optionsOverride, setOptionsOverride] = useState<{
    cities: Option[];
    provinces: Option[];
    suburbs: Option[];
    tags: Option[];
    postalCodes: Option[];
  }>({ cities: [], provinces: [], suburbs: [], tags: [], postalCodes: [] });

  // Fetch comprehensive filter options directly from customers
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const { data: customers, error } = await supabase
          .from('customers')
          .select('city, province_state, suburb, postal_code, tags');
        if (error) throw error;

        const citiesSet = new Set<string>();
        const provincesSet = new Set<string>();
        const suburbsSet = new Set<string>();
        const tagsSet = new Set<string>();
        const postalCodesSet = new Set<string>();

        (customers || []).forEach((c: any) => {
          if (c.city) citiesSet.add(c.city);
          if (c.province_state) provincesSet.add(c.province_state);
          if (c.suburb) suburbsSet.add(c.suburb);
          if (c.postal_code) postalCodesSet.add(c.postal_code);
          if (Array.isArray(c.tags)) c.tags.filter(Boolean).forEach((t: string) => tagsSet.add(t));
        });

        setOptionsOverride({
          cities: Array.from(citiesSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          provinces: Array.from(provincesSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          suburbs: Array.from(suburbsSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          tags: Array.from(tagsSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          postalCodes: Array.from(postalCodesSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
        });
      } catch (err) {
        console.error('Error fetching credit note filter options:', err);
      }
    };
    fetchOptions();
  }, []);

  // Build dynamic options with fallback to credits array data
  const dynamicOptions = useMemo(() => {
    const cities = [...new Set(credits.map(c => c.customer?.city).filter(Boolean))]
      .map(v => ({ value: v!, label: v! })).sort((a, b) => a.label.localeCompare(b.label));
    const provinces = [...new Set(credits.map(c => c.customer?.province_state).filter(Boolean))]
      .map(v => ({ value: v!, label: v! })).sort((a, b) => a.label.localeCompare(b.label));
    const suburbs = [...new Set(credits.map(c => c.customer?.suburb).filter(Boolean))]
      .map(v => ({ value: v!, label: v! })).sort((a, b) => a.label.localeCompare(b.label));
    const tags = [...new Set(credits.flatMap(c => c.customer?.tags || []).filter(Boolean))]
      .map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label));
    const postalCodes = [...new Set(credits.map(c => c.customer?.postal_code).filter(Boolean))]
      .map(v => ({ value: v!, label: v! })).sort((a, b) => a.label.localeCompare(b.label));

    return {
      cities: optionsOverride.cities.length ? optionsOverride.cities : cities,
      provinces: optionsOverride.provinces.length ? optionsOverride.provinces : provinces,
      suburbs: optionsOverride.suburbs.length ? optionsOverride.suburbs : suburbs,
      tags: optionsOverride.tags.length ? optionsOverride.tags : tags,
      postalCodes: optionsOverride.postalCodes.length ? optionsOverride.postalCodes : postalCodes,
    };
  }, [credits, optionsOverride]);

  const filterEntities: FilterEntity[] = [
    { key: 'credit', label: 'Credit' },
    { key: 'customer', label: 'Customer' },
  ];

  const filterAttributesByEntity: Record<string, FilterAttribute[]> = {
    credit: [
      { key: 'status', label: "'s Status", type: 'select', options: [
        { value: 'issued', label: 'Issued' },
        { value: 'applied', label: 'Applied' },
        { value: 'refunded', label: 'Refunded' },
        { value: 'cancelled', label: 'Cancelled' },
      ]},
      { key: 'source_type', label: "'s Source Type", type: 'select', options: [
        { value: 'overpayment', label: 'Overpayment' },
        { value: 'manual', label: 'Manual' },
      ]},
      { key: 'reason_type', label: "'s Reason", type: 'select', options: ALL_CREDIT_REASON_TYPES.map(r => ({ value: r.value, label: r.label })) },
      { key: 'credit_note_number', label: "'s Number", type: 'text' },
      { key: 'credit_amount', label: "'s Amount", type: 'number' },
      { key: 'remaining_amount', label: "'s Remaining", type: 'number' },
      { key: 'created_at', label: "'s Date Created", type: 'date' },
      { key: 'description', label: "'s Description", type: 'text' },
    ],
    customer: [
      { key: 'name', label: "'s Name", type: 'text' },
      { key: 'customer_type', label: "'s Type", type: 'select', options: [
        { value: 'residential', label: 'Residential' },
        { value: 'commercial', label: 'Commercial' },
      ]},
      { key: 'city', label: "'s City", type: 'select', options: dynamicOptions.cities },
      { key: 'province_state', label: "'s Province", type: 'select', options: dynamicOptions.provinces },
      { key: 'suburb', label: "'s Suburb", type: 'select', options: dynamicOptions.suburbs },
      { key: 'postal_code', label: "'s Postal Code", type: 'select', options: dynamicOptions.postalCodes },
      { key: 'tags', label: "'s Tags", type: 'select', options: dynamicOptions.tags },
      { key: 'lead_source', label: "'s Lead Source", type: 'select', options: [
        { value: 'Word of Mouth', label: 'Word of Mouth' },
        { value: 'Website', label: 'Website' },
        { value: 'Facebook', label: 'Facebook' },
        { value: 'Instagram', label: 'Instagram' },
        { value: 'LinkedIn', label: 'LinkedIn' },
        { value: 'Social Media', label: 'Social Media (Other)' },
        { value: 'Google', label: 'Google' },
        { value: 'Car Branding', label: 'Car Branding' },
        { value: 'House Branding', label: 'House Branding' },
        { value: 'Other', label: 'Other (Unspecified)' },
      ]},
      { key: 'email', label: "'s Email", type: 'text' },
      { key: 'phone', label: "'s Phone", type: 'text' },
    ],
  };

  const filterFields: FilterField[] = useMemo(() => {
    const fields: FilterField[] = [];
    filterAttributesByEntity.credit?.forEach(attr => {
      fields.push({ key: `credit.${attr.key}`, label: `Credit${attr.label}`, type: attr.type, options: attr.options });
    });
    filterAttributesByEntity.customer?.forEach(attr => {
      fields.push({ key: `customer.${attr.key}`, label: `Customer${attr.label}`, type: attr.type, options: attr.options });
    });
    return fields;
  }, [dynamicOptions]);

  const addGroup = useCallback(() => {
    const newGroup: FilterGroup = {
      id: `group_${Date.now()}_${Math.random()}`,
      rules: [{
        id: `rule_${Date.now()}_${Math.random()}`,
        entity: '',
        attribute: '',
        operator: 'equals',
        value: null,
        logic: undefined,
      }],
      logic: 'AND',
    };
    setDraftGroups(prev => [...prev, newGroup]);
  }, []);

  const updateGroup = useCallback((groupId: string, updates: Partial<FilterGroup>) => {
    setDraftGroups(prev => prev.map(g => g.id === groupId ? { ...g, ...updates } : g));
  }, []);

  const removeGroup = useCallback((groupId: string) => {
    setDraftGroups(prev => prev.filter(g => g.id !== groupId));
  }, []);

  const addRuleToGroup = useCallback((groupId: string) => {
    const newRule: FilterRule = {
      id: `rule_${Date.now()}_${Math.random()}`,
      entity: '',
      attribute: '',
      operator: 'equals',
      value: null,
      logic: 'AND',
    };
    setDraftGroups(prev => prev.map(g => {
      if (g.id === groupId) {
        return { ...g, rules: [...g.rules, { ...newRule, logic: g.rules.length > 0 ? 'AND' : undefined }] };
      }
      return g;
    }));
  }, []);

  const updateRuleInGroup = useCallback((groupId: string, ruleId: string, updates: Partial<FilterRule>) => {
    setDraftGroups(prev => prev.map(g => {
      if (g.id === groupId) {
        return { ...g, rules: g.rules.map(r => r.id === ruleId ? { ...r, ...updates } : r) };
      }
      return g;
    }));
  }, []);

  const removeRuleFromGroup = useCallback((groupId: string, ruleId: string) => {
    setDraftGroups(prev => prev.map(g => {
      if (g.id === groupId) {
        const updatedRules = g.rules.filter(r => r.id !== ruleId);
        if (updatedRules.length === 0) return null;
        if (updatedRules.length > 0) updatedRules[0].logic = undefined;
        return { ...g, rules: updatedRules };
      }
      return g;
    }).filter(Boolean) as FilterGroup[]);
  }, []);

  const clearAllRules = useCallback(() => {
    setDraftGroups([]);
    setAppliedGroups([]);
  }, []);

  const isRuleComplete = useCallback((rule: FilterRule) => {
    if (!rule.entity || !rule.attribute || !rule.operator) return false;
    if (['is_empty', 'is_not_empty'].includes(rule.operator)) return true;
    if (rule.operator === 'between') {
      if (rule.attribute.includes('date') || rule.attribute === 'created_at') {
        return rule.value?.from && rule.value?.to;
      }
      return rule.value?.min !== undefined && rule.value?.max !== undefined;
    }
    return rule.value !== null && rule.value !== undefined && rule.value !== '';
  }, []);

  const applyFilters = useCallback(() => {
    const completeGroups = draftGroups.map(g => ({
      ...g,
      rules: g.rules.filter(isRuleComplete),
    })).filter(g => g.rules.length > 0);
    setAppliedGroups(completeGroups);
  }, [draftGroups, isRuleComplete]);

  const hasUnappliedChanges = useMemo(() => {
    return JSON.stringify(draftGroups) !== JSON.stringify(appliedGroups);
  }, [draftGroups, appliedGroups]);

  const canApplyFilters = useMemo(() => {
    return draftGroups.length > 0 && draftGroups.some(g =>
      g.rules.length > 0 && g.rules.every(isRuleComplete)
    );
  }, [draftGroups, isRuleComplete]);

  const getFieldValue = useCallback((credit: CreditWithCustomer, rule: FilterRule) => {
    if (rule.entity === 'credit') {
      switch (rule.attribute) {
        case 'status': return credit.status;
        case 'source_type': return credit.source_type;
        case 'reason_type': return credit.reason_type || '';
        case 'credit_note_number': return credit.credit_note_number || '';
        case 'credit_amount': return credit.credit_amount;
        case 'remaining_amount': return credit.remaining_amount;
        case 'created_at': return credit.created_at;
        case 'description': return credit.description || '';
        default: return '';
      }
    }
    if (rule.entity === 'customer') {
      switch (rule.attribute) {
        case 'name': return credit.customer ? `${credit.customer.name} ${credit.customer.last_name}` : '';
        case 'customer_type': return credit.customer?.customer_type || '';
        case 'city': return credit.customer?.city || '';
        case 'province_state': return credit.customer?.province_state || '';
        case 'suburb': return credit.customer?.suburb || '';
        case 'tags': return credit.customer?.tags || [];
        case 'postal_code': return credit.customer?.postal_code || '';
        case 'lead_source': return credit.customer?.lead_source || '';
        case 'email': return credit.customer?.emails?.join(', ') || '';
        case 'phone': return credit.customer?.cell_numbers?.join(', ') || '';
        default: return '';
      }
    }
    return '';
  }, []);

  const evaluateRule = useCallback((credit: CreditWithCustomer, rule: FilterRule) => {
    if (!rule.entity || !rule.attribute || !rule.operator) return false;
    const fieldValue = getFieldValue(credit, rule);

    switch (rule.operator) {
      case 'equals':
        if (Array.isArray(rule.value)) {
          if (Array.isArray(fieldValue)) return rule.value.some(v => fieldValue.includes(v));
          return rule.value.includes(fieldValue);
        }
        return fieldValue === rule.value;
      case 'not_equals':
        if (Array.isArray(rule.value)) {
          if (Array.isArray(fieldValue)) return !rule.value.some(v => fieldValue.includes(v));
          return !rule.value.includes(fieldValue);
        }
        return fieldValue !== rule.value;
      case 'contains':
        return String(fieldValue).toLowerCase().includes(String(rule.value).toLowerCase());
      case 'not_contains':
        return !String(fieldValue).toLowerCase().includes(String(rule.value).toLowerCase());
      case 'greater_than':
        if (rule.attribute === 'created_at') return new Date(fieldValue as string) > new Date(rule.value);
        return Number(fieldValue) > Number(rule.value);
      case 'less_than':
        if (rule.attribute === 'created_at') return new Date(fieldValue as string) < new Date(rule.value);
        return Number(fieldValue) < Number(rule.value);
      case 'between':
        if (rule.attribute === 'created_at') {
          const d = new Date(fieldValue as string);
          return d >= new Date(rule.value.from) && d <= new Date(rule.value.to);
        }
        const num = Number(fieldValue);
        return num >= rule.value.min && num <= rule.value.max;
      case 'is_empty':
        return !fieldValue || fieldValue === '';
      case 'is_not_empty':
        return !!fieldValue && fieldValue !== '';
      default:
        return false;
    }
  }, [getFieldValue]);

  const filteredCredits = useMemo(() => {
    if (appliedGroups.length === 0) return credits;

    return credits.filter(credit => {
      const groupResults: boolean[] = [];

      for (const group of appliedGroups) {
        if (group.rules.length === 0) continue;
        let groupResult = true;

        for (let i = 0; i < group.rules.length; i++) {
          const rule = group.rules[i];
          const ruleResult = evaluateRule(credit, rule);
          if (i === 0) {
            groupResult = ruleResult;
          } else {
            const logic = rule.logic || 'AND';
            groupResult = logic === 'AND' ? groupResult && ruleResult : groupResult || ruleResult;
          }
        }
        groupResults.push(groupResult);
      }

      if (groupResults.length === 0) return true;
      let finalResult = groupResults[0];
      for (let i = 1; i < groupResults.length; i++) {
        const gl = appliedGroups[i].logic;
        finalResult = gl === 'AND' ? finalResult && groupResults[i] : finalResult || groupResults[i];
      }
      return finalResult;
    });
  }, [credits, appliedGroups, evaluateRule]);

  const hasActiveFilters = appliedGroups.length > 0;

  return {
    draftGroups,
    appliedGroups,
    filteredCredits,
    hasActiveFilters,
    hasUnappliedChanges,
    canApplyFilters,
    addGroup,
    updateGroup,
    removeGroup,
    addRuleToGroup,
    updateRuleInGroup,
    removeRuleFromGroup,
    clearAllRules,
    applyFilters,
    filterFields,
    filterEntities,
    filterAttributesByEntity,
  };
};
